import type { Product } from '@infinix/tokens';

import { audienceOfRoles, permissionsOfRoles, type Audience, type Permission } from '@infinix/contract';

/* Permissions and Core roles live in the shared contract (web and backends enforce the same map). */
export {
  PERMISSIONS, CORE_ROLES, STAFF_ROLES, isStaffPermission, roleName, permissionsOfRoles, audienceOfRoles,
  type Permission, type Audience,
} from '@infinix/contract';

/** Who is signed in, as far as app access goes: their Core roles and the products of their business units. */
export interface Identity {
  roles: readonly string[];
  products: readonly Product[];
}

export const permissionsOf = (identity: Identity): Set<Permission> => permissionsOfRoles(identity.roles);
export const audienceOf = (identity: Identity): Audience => audienceOfRoles(identity.roles);

/* =========================================================================================
 * App manifests: each console publishes one; the shell builds launcher and nav from them.
 * ========================================================================================= */

export interface NavItem {
  label: string;
  path: string;
  icon: string;
  perm?: Permission;
  /**
   * Shown to Nest staff at "All tenants" scope when they hold this permission. Items without it are tenant-scope
   * only (staff reach them by picking a tenant). Only for customer apps that open to staff (AppEntry.staffScope).
   */
  platformPerm?: Permission;
  /** Also shown when the person holds any of these (e.g. a payments auditor sees Organization → Audit log). */
  anyPerm?: Permission[];
  /** Live count shown in the rail (nav badges). */
  badgeKey?: string;
  badgeTone?: 'warning' | 'info' | 'danger' | 'neutral';
}

export interface NavGroup { label?: string; items: NavItem[]; footer?: boolean }

/** What the launcher knows about an app (the catalog entry). */
export interface AppEntry {
  id: string;
  name: string;
  description: string;
  icon: string;
  /** App identity: icon tile and accent (active nav, primary buttons, focus, charts). Status colours never change per app. */
  accent: { light: string; dark: string };
  basePath: string;
  audience: Audience;
  /** Shown when the user holds any of these. */
  anyPermission: Permission[];
  /** Shown only if the tenant has this product (product apps). */
  product?: Product;
  /** Product apps live in their own codebase and adopt the shell. */
  linked?: { status: string };
  /** Every signed-in person has this app, customer or staff (My account). */
  everyone?: boolean;
  /**
   * A customer app Nest staff also open, as the tenant's admins see it, when they hold any of these (one source, no Ops
   * copy). `allTenants`: it also works with no organization picked (across every tenant); otherwise the launcher offers
   * it only once staff pick an organization.
   */
  staffScope?: { anyPermission: Permission[]; allTenants?: boolean };
  /**
   * False: built and deployed, but offered to no one yet (not in the launcher, its address shows "no access") until
   * its first screens work. Nobody meets an empty app.
   */
  offered?: boolean;
  /**
   * The records this app owns that a payment can belong to: the business reference key a product puts on its payments,
   * and the screen that opens one (`:id`). Payments shows any payment's references and links them through this, so it
   * never knows a product (A-36). A reference may name several records, comma-separated.
   */
  records?: { ref: string; label: string; path: string }[];
  /**
   * The screen that finds this app's records for a payment of its product that carries no reference to them (`:id`, the
   * Core transaction id): e.g. a bill paid online, which ECOM matched at capture.
   */
  paymentRecords?: { label: string; path: string };
}

export interface RecordLink { app: AppEntry; ref: string; label: string; id: string; path: string }

const RECORD_ID = /^[A-Za-z0-9_-]{1,100}$/;

/**
 * The links a payment's business references open, in the apps given (pass the apps the person may open). `product`: the
 * product the payment's business unit runs (a unit runs one product), when known: a product app links only its own
 * product's payments, so another product's reference of the same name (e.g. a Retail order's invoiceId) opens nothing.
 */
export function recordLinks(apps: AppEntry[], references: Record<string, string>, product?: string | null): RecordLink[] {
  return apps.filter((app) => !product || !app.product || app.product === product).flatMap((app) => (app.records ?? []).flatMap((r) => {
    const value = references[r.ref];
    if (!value) return [];
    return value.split(',').map((x) => x.trim()).filter((id) => RECORD_ID.test(id))
      .map((id) => ({ app, ref: r.ref, label: r.label, id, path: r.path.replace(':id', encodeURIComponent(id)) }));
  }));
}

/**
 * Where to find a payment's records when its references open none: the apps given (pass the apps the person may open)
 * that run the payment's product and declare a lookup. Product unknown: none (a lookup is product-specific).
 */
export function paymentRecordLookups(apps: AppEntry[], transactionId: string, product: string | null | undefined): { app: AppEntry; label: string; path: string }[] {
  if (!product || !RECORD_ID.test(transactionId)) return [];
  return apps.filter((app) => app.product === product && app.paymentRecords)
    .map((app) => ({ app, label: app.paymentRecords!.label, path: app.paymentRecords!.path.replace(':id', encodeURIComponent(transactionId)) }));
}

/** An app's own manifest: its catalog entry plus its menu, which only the app itself ships. */
export interface AppManifest extends AppEntry {
  nav: NavGroup[];
}

/**
 * The apps a person may open. `staffTenantAccess` is false until Core's staff route set is configured (N-7): customer
 * apps then stay closed to staff even if they declare a staff scope.
 */
export function visibleApps<T extends AppEntry>(identity: Identity | null, apps: T[], staffTenantAccess = false): T[] {
  if (!identity) return [];
  const perms = permissionsOf(identity);
  const audience = audienceOf(identity);
  return apps.filter((a) => {
    if (a.offered === false) return false;
    if (a.everyone) return true;
    // Staff in a tenant's app: the tenant's products are the products of its units (identity.products), as its admins.
    if (a.product && !identity.products.includes(a.product)) return false;
    if (a.audience !== audience) return Boolean(staffTenantAccess && a.staffScope && audience === 'staff' && a.staffScope.anyPermission.some((p) => perms.has(p)));
    if (a.product) return true;
    return a.anyPermission.some((p) => perms.has(p));
  });
}

export { CATALOG, catalogEntry, appForPath } from './catalog';
export { planStaffScope, readStaffScope, returnAfterSignIn, staffScopeSearch, tenantHome, type ScopePlan, type StaffScope, type ScopeApp } from './staffScope';
export { accessOf, actionState, menuItemVisible, NO_ROLE_REASON, STAFF_VIEW_REASON, type Access, type Viewer } from './access';
